
import { defineComponent, reactive, toRefs, onMounted, watch } from 'vue'
import Charts from '@/components/page/charts.vue'
import Nodata from '@/components/page/nodata.vue'
import globalsets from '@/utils/globalsets'
import f10ApiHk from "@/api/f10ApiHk"
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { Charts, Nodata },
  props: {
    itemdata: { type: Object, default: () => ({}) },
    pageSourceData: { type: Object, default: () => ({}) },
    colorList: { type: Object, default: () => ({}) },
    token: {
      type: String,
      default: () => {
        return ''
      }
    },
    theme: {
      type: String,
      default: 'light'
    }
  },
  setup(props) {
    const route = useRoute()
    const state: any = reactive({
      productData: [],
      regionData: [],
      currentProductData: [],
      currentRegionData: [],
      reportOptions: [],
      selectedReport: "",
      mergedTableData: [],
      spinning: true,
      productChartOption: {},
      regionChartOption: {},
      color: globalsets.getColor(),
      getPopupContainer: (e: HTMLElement) => e.parentNode,
      // 列定义：category 列既提供 customCell（推荐）也提供 customRender（兼容）
      columns: [
        {
          title: '分类',
          dataIndex: 'category',
          key: 'category',
          width: '90px',
          align: 'left',
          // 推荐（ant-design-vue 新版本）
          customCell: (record: any) => {
            return { rowSpan: record.rowSpan ?? 0 }
          },
          // 兼容老版本：通过 customRender 返回 attrs
          customRender: ({ text, record }: any) => {
            return {
              children: text,
              attrs: { rowSpan: record.rowSpan ?? 0 }
            }
          }
        },
        {
          title: '业务名称',
          dataIndex: 'projectName',
          key: 'projectName',
          ellipsis: true
        },
        {
          title: '主营收(元)',
          dataIndex: 'revenue',
          key: 'revenue',
          align: 'right'
        },
        {
          title: '收入占比',
          dataIndex: 'proportion',
          key: 'proportion',
          align: 'right'
        }
      ]
    })

    const reportTypeMap: any = { 1: '一季报', 2: '中报', 3: '三季报(累积)', 4: '年报' }

    const formatRevenue = (revenue: number) => {
      if (revenue === null || revenue === undefined) return '—'

      const absRevenue = Math.abs(revenue)
      const sign = revenue < 0 ? '-' : ''

      if (absRevenue >= 1e8) return `${sign}${(absRevenue / 1e8).toFixed(2)}亿`
      if (absRevenue >= 1e4) return `${sign}${(absRevenue / 1e4).toFixed(2)}万`
      // 如需千分位可以在这里扩展
      return revenue.toString()
    }

    const processApiResponse = (apiData: any) => {
      if (!apiData?.list || !Array.isArray(apiData.list)) return []
      return apiData.list.map((report: any) => ({
        reportType: report.reportType,
        statementYear: report.statementYear,
        revenueTotal: report.revenueTotal,
        list: (report.list || []).map((item: any) => ({
          projectName: item.projectName || '—',
          revenue: item.revenue || 0,
          proportion: item.proportion || 0
        }))
      }))
    }

    const fetchProductData = async () => {
      try {
        const res = await f10ApiHk.getBriefBriefManagementMg({
          data: {
            //  market: "NASDAQ", 
            //  symbol: "AAPL", 
            market: route.query.market || "NASDAQ",
            symbol: route.query.code || "AAPL",
            classifyStandard: 1,
            showType: 2
          },
          headers: { token: props.token || "3d5eff0d3c7ac7f3af30c337dcdf6944" }
        })
        state.productData = processApiResponse((res as any).data)
        return true
      } catch (e) {
        console.error('获取产品数据失败:', e)
        state.productData = []
        return false
      }
    }

    const fetchRegionData = async () => {
      try {
        const res = await f10ApiHk.getBriefBriefManagementMg({
          data: {
            //  market: "NASDAQ", 
            //  symbol: "AAPL", 
            market: route.query.market || "NASDAQ",
            symbol: route.query.code || "AAPL",
            classifyStandard: 2,
            showType: 2
          },
          headers: { token: props.token || "3d5eff0d3c7ac7f3af30c337dcdf6944" }
        })
        state.regionData = processApiResponse((res as any).data)
        return true
      } catch (e) {
        console.error('获取地区数据失败:', e)
        state.regionData = []
        return false
      }
    }

    // 统一的数据加载函数，确保两个接口都完成后再渲染
    const loadAllData = async () => {
      state.spinning = true
      try {
        // 并行请求两个接口
        const [productSuccess, regionSuccess] = await Promise.all([
          fetchProductData(),
          fetchRegionData()
        ])

        console.log('数据加载完成:', {
          productSuccess,
          regionSuccess,
          productDataLength: state.productData.length,
          regionDataLength: state.regionData.length
        })

        // 两个接口都完成后，生成报告选项和更新图表数据
        generateReportOptions()
        if (state.reportOptions.length) {
          updateChartData()
        }
      } catch (error) {
        console.error('数据加载失败:', error)
      } finally {
        state.spinning = false
      }
    }

    const generateReportOptions = () => {
      const all = (state.productData || []).concat(state.regionData || [])
      const unique = new Set<string>()
      all.forEach((r: any) => {
        const label = `${r.statementYear}年${reportTypeMap[r.reportType] || '未知报'}`
        const value = `${r.statementYear}-${r.reportType}`
        unique.add(JSON.stringify({ label, value }))
      })
      state.reportOptions = Array.from(unique).map(s => JSON.parse(s)).sort((a: any, b: any) => {
        const [aYear, aType] = a.value.split('-').map(Number)
        const [bYear, bType] = b.value.split('-').map(Number)
        return bYear - aYear || bType - aType
      })
      if (state.reportOptions.length) state.selectedReport = state.reportOptions[0].value
    }

    // 关键：构建 mergedTableData，**每个业务一行**，第一行 rowSpan = group.length，其他行 rowSpan = 0
    const updateChartData = () => {
      try {
        const currentProduct = state.productData.find((r: any) => `${r.statementYear}-${r.reportType}` === state.selectedReport)
        const currentRegion = state.regionData.find((r: any) => `${r.statementYear}-${r.reportType}` === state.selectedReport)
        const productList = currentProduct?.list || []
        const regionList = currentRegion?.list || []

        // 更新当前显示的数据
        state.currentProductData = productList
        state.currentRegionData = regionList

        const newTable: any[] = []
        let categoryId = 0
        let rowIndex = 0

        const pushCategoryRows = (list: any[], name: string) => {
          if (!list || list.length === 0) return
          categoryId++
          list.forEach((item: any, idx: number) => {
            newTable.push({
              key: `${name}-${idx}`,
              categoryId,
              category: name,
              projectName: item.projectName,
              revenue: formatRevenue(item.revenue),
              proportion: `${(item.proportion || 0).toFixed(2)}%`,
              // **严格数字 rowSpan**：第一行为组长度，其它为 0
              rowSpan: idx === 0 ? list.length : 0,
              rowIndex: rowIndex++
            })
          })
        }

        pushCategoryRows(productList, '按产品')
        pushCategoryRows(regionList, '按地区')

        state.mergedTableData = newTable

        // 更新图表（保持你原逻辑）
        generateChartOptions(productList, regionList)
      } catch (err) {
        console.error('更新图表数据失败:', err)
      }
    }

    const generateChartOptions = (productList: any[], regionList: any[]) => {
      const baseLegend = (data: any[]) => ({
        show: true, orient: 'vertical', left: 230, top: 'middle',
        data: data.map(d => d.projectName),
        textStyle: {
          color: props.theme === 'dark' ? '#d7d7df' : '#8c94a8',

          padding: [2, 0, 0, 0] // 上边距
        },
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 8,
        itemColor: props.theme === 'dark' ? '#d7d7df' : '#8c94a8',
        formatter: function (name: string) {
          // 如果名称太长，进行换行处理
          if (name.length > 8) {
            return name.substring(0, 6) + '\n' + name.substring(6);
          }
          return name;
        }
      })
      if (productList.length) {
        state.productChartOption = {
          legend: baseLegend(productList),
          tooltip: {
            trigger: 'item',
            position: function (point: any, params: any, dom: any, rect: any, size: any) {
              // 始终显示在鼠标右边
              return [point[0] + 10, point[1] - size.contentSize[1] / 2];
            },
            formatter: (p: any) => `${p.name}: ${formatRevenue(p.value)} (${p.percent}%)`
          },
          series: [{
            name: '按产品',
            type: 'pie',
            radius: '50%',
            center: ['50%', '50%'],
            data: productList.map((i: any, index: number) => ({
              name: i.projectName,
              value: i.revenue,
              itemStyle: {
                color: i.revenue < 0 ? '#FF4D4F' : state.color[index % state.color.length]
              }
            })),
            label: { show: false },
            emphasis: { label: { show: false } }
          }]
        }
      } else {
        state.productChartOption = {}
      }
      if (regionList.length) {
        state.regionChartOption = {
          legend: baseLegend(regionList),
          tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}: ${formatRevenue(p.value)} (${p.percent}%)` },
          series: [{
            name: '按地区',
            type: 'pie',
            radius: '50%',
            center: ['50%', '50%'],
            data: regionList.map((i: any, index: number) => ({
              name: i.projectName,
              value: i.revenue,
              itemStyle: {
                color: i.revenue < 0 ? '#FF4D4F' : state.color[index % state.color.length]
              }
            })),
            label: { show: false },
            emphasis: { label: { show: false } }
          }]
        }
      } else {
        state.regionChartOption = {}
      }
    }

    const handleChange = () => updateChartData()

    onMounted(() => {
      loadAllData()
    })

    // 移除watch，因为数据加载完成后会直接调用相关函数
    // watch(() => [state.productData, state.regionData], () => {
    //   generateReportOptions()
    //   if (state.reportOptions.length) updateChartData()
    // }, { deep: true })

    return {
      ...toRefs(state),
      handleChange,

    }
  }
})
